import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@hotjar/hotsauce';
import { LOGIN } from '@hotjar/webapp/constants/routes/auth';
import { getRoute } from '@hotjar/webapp/utils/routing';
import type { CurrentUser } from '@hotjar/user';
import { useCurrentUser } from '@hotjar/user';
import { useRegistrationCampaign } from '@hotjar/webapp/hooks/useCampaigns';
import { useQueryParameter } from '@hotjar/webapp/hooks/useQueryParameter';
import { useEffectOnceWithDependencies } from '@hotjar/webapp/hooks/useEffectOnceWithDependencies';
import type { OnboardingDetails } from '@hotjar/webapp/domains/activation/onboarding/model';
import { onboard } from '@hotjar/webapp/domains/activation/onboarding/api';
import { bus, currentSiteSet, refreshData } from '@hotjar/webapp/utils/events';
import { useCurrentSite, mapSiteDtoToModel } from '@hotjar/site';
import { BASE as OVERVIEW, SETUP } from '@hotjar/webapp/constants/routes/overview';
import { STEPS } from '@hotjar/webapp/features/Activation/SetupWizard/steps';
import { SURVEYS } from '@hotjar/webapp/constants/routes';
import type { Site } from '@hotjar/site';
import { noop } from '@hotjar/common';
import { mark, measureAndLog } from '@hotjar/performance';
import { MARKS } from '@hotjar/webapp/performance';
import { hjGetCookie } from '@hotjar/webapp/utils/cookies';
import {
  applyCampaignToAccount,
  getRegistrationCampaign,
} from '@hotjar/webapp/domains/campaigns/api';
import { refreshFromServer } from '@hotjar/webapp/domains/common/user-data';
import { isDemoSiteId } from '@hotjar/webapp/domains/acquisition/demo/demo-site-redirections';
import { useInvalidatePermissionQuery } from '@hotjar/auth';
import { useOnboardingTracking } from './useOnboardingTracking';
import { useSurveyTemplate } from './useSurveyTemplate';
import { useOnboardingNavigation } from '../steps/OrganizationSteps/hooks/useOnboardingNavigation';

interface OnboardingData {
  user: CurrentUser | undefined;
  isOnboarding: boolean;
  isLoading: boolean;
  onboard: (details: OnboardingDetails) => Promise<void>;
}

export const useOnboarding = (): OnboardingData => {
  const {
    trackLogin,
    trackFormSubmitted,
    trackSignupStepLoaded,
    trackSignupStepSubmitted,
    trackSignupStepCompleted,
    trackHotjarInstallerOpened,
    trackOnboardingStarted,
  } = useOnboardingTracking();
  const { t } = useTranslation();
  const campaignId = useQueryParameter('c');
  const { clearOrgDetails } = useOnboardingNavigation();
  const { data: campaign, isLoading: isLoadingCampaign } = useRegistrationCampaign(campaignId);
  const {
    data: user,
    isLoading: isCurrentUserLoading,
    refetch: refetchCurrentUser,
  } = useCurrentUser();

  if (!isCurrentUserLoading && !user) {
    /**
     * It is expected that during onboarding a user is already created.
     * In cases no user is fetched from useCurrentUser, chance is the user is in the process of being
     * persisted into the system, possibly as a result of the Google SSO signup flow.
     * In this case a refetch is required.
     */
    setTimeout(() => {
      refetchCurrentUser();
    }, 1000);
  }

  const { surveyTemplateId, preserveTemplateId: preserveSurveyTemplateId } = useSurveyTemplate();
  const {
    data: initialCurrentSite,
    isFetched: isCurrentSiteFetched,
    isLoading: isCurrentSiteLoading,
  } = useCurrentSite();
  const [isOnboarding, setIsOnboarding] = useState(false);
  const { showToast } = useNotify();
  const navigate = useNavigate();
  const partnerStackKey = hjGetCookie('growSumoPartnerKey') || undefined;
  const invalidatePermissions = useInvalidatePermissionQuery();

  useEffect(() => {
    if (user && (campaign || campaignId === '')) {
      trackSignupStepLoaded(campaign);
    }
  }, [user, campaign, campaignId, trackSignupStepLoaded]);

  useEffectOnceWithDependencies(
    () => {
      if (initialCurrentSite?.id) {
        navigate(
          getRoute(OVERVIEW.path, {
            siteId: initialCurrentSite.id,
          }),
          { replace: true },
        );
      }
    },
    [initialCurrentSite?.id, navigate],
    () => !!isCurrentSiteFetched,
  );

  const handleSignupError = () => {
    trackFormSubmitted(false);
    showToast({
      kind: 'danger',
      text: t('activation:onboarding.onboarding-error'),
    });
    // We're setting isOnboarding to false only if onboarding fails
    // and we want to stay at the /onboarding page
    // otherwise we'll enable the submit button right before redirecting
    // the user to insights, which can result in accidental submittion.
    setIsOnboarding(false);
  };

  const redirect = (site: Site, organizationSizeMin: number) => {
    const atLeastThousand = organizationSizeMin >= 1000;
    const siteId = site.id;
    if (!atLeastThousand) {
      trackHotjarInstallerOpened(site);
      navigate(`${getRoute(SETUP.path, { siteId })}?step=${STEPS.SITE_URL}&onboarding=true`, {
        replace: true,
      });
    } else if (surveyTemplateId) {
      navigate(
        getRoute(SURVEYS.CREATE.path, {
          templateId: surveyTemplateId,
          siteId,
          activeFlowSection: 'name',
        }),
        { replace: true },
      );
    } else {
      navigate(
        getRoute(OVERVIEW.path, {
          siteId,
        }),
        { replace: true },
      );
    }
  };

  const updateUserResourcesAndLogin = () =>
    refreshFromServer()
      .then(async (userData) => {
        // we grab the site which is not demosite
        const siteDTO = userData.sites.find(
          (site) => !isDemoSiteId(site.id) && Number(site.id) !== 14,
        );
        const site = siteDTO ? mapSiteDtoToModel(siteDTO) : undefined;

        if (site?.id && user?.id) {
          trackLogin();
          bus.publish(currentSiteSet({ site }));
          await preserveSurveyTemplateId(user, site);
          bus.publish(refreshData(noop));
        }

        // Perf: Measure how long it took for the user to be fully onboarded, after he's been logged in
        measureAndLog({
          name: MARKS.SIGN_UP_ONBOARDING_SUBMISSION_TIME,
          startMark: MARKS.SIGN_UP_ONBOARDING_SUBMISSION_START,
        });
        return site;
      })
      .catch(() => {
        // Redirect to login page if we fail to log the user in automatically
        showToast({ kind: 'success', text: 'Successfully registered, moving to login...' });
        navigate(LOGIN.path, { replace: true });
      });

  const handleOnboarding = async (onboardingDetails: OnboardingDetails) => {
    // Perf: Marks the start time of the onboarding process within the Performance API
    mark(MARKS.SIGN_UP_ONBOARDING_SUBMISSION_START);
    setIsOnboarding(true);
    trackSignupStepSubmitted(campaign);
    await onboard({ ...onboardingDetails, partnerStackKey })
      .then(() => {
        trackSignupStepCompleted(campaign);
        trackFormSubmitted(true);
      })
      .then(updateUserResourcesAndLogin)
      .then(async (site) => {
        if (site) {
          trackOnboardingStarted(site);
        }

        if (user?.id) {
          clearOrgDetails(user.id);
        }
        if (!site?.id || !user?.id) return null;

        await invalidatePermissions();

        const CAMPAIGN_ID = 'a86dd6a983b5480dabf306c8f1f95b26';
        const isCampaignValid = await getRegistrationCampaign(CAMPAIGN_ID);
        const campaignSampleRate = campaign?.details?.sampleRate;

        if (
          isCampaignValid &&
          (onboardingDetails.organizationSize === '101-250' ||
            onboardingDetails.organizationSize === '251-1000') &&
          campaign?.plan === 'business' &&
          campaignSampleRate &&
          campaignSampleRate < 200000
        ) {
          await applyCampaignToAccount({
            campaignId: CAMPAIGN_ID,
            accountId: JSON.stringify(site?.account.id),
          });
        }

        redirect(site, onboardingDetails.organizationSizeMin);

        return site.id;
      })
      .catch(() => {
        handleSignupError();
        setIsOnboarding(false);
      });
  };

  return {
    user,
    onboard: handleOnboarding,
    isLoading: isCurrentUserLoading || isLoadingCampaign || isCurrentSiteLoading,
    isOnboarding,
  };
};
